'use client';

import { usePrint } from '@/lib/hooks';
import { PageProps } from '@/lib/types';
import { Section as AppSection, Breadcrumb, Empty, LinkBox } from '@/ui';
import { Content, PaginationWidget } from '@/widgets';
import { Container } from '@mantine/core';
import { useParams, useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import useSWR from 'swr';

type SectionItem = {
  id: number;
  category_id: number;
  title: string;
  link: string;
  anons: string;
  anons_image: string;
  category_title: string;
};

type Section = PageProps & {
  section_title: string;
  data?: SectionItem[];
};

type Props = {
  section: Section;
};

export default function Section({ section }: Props) {
  const { section_code } = useParams();
  const searchParams = useSearchParams();
  const { ref, handlePrint } = usePrint();

  const { data: sections, isLoading } = useSWR<Section>([
    '/authorities/lists/sections',
    { params: { code_name: section_code, page: searchParams.get('page') || 1 } },
  ]);

  const breadcrumbItems = [
    { title: section?.breadcrumb, href: `/general-menu/${section?.menu_id}` },
    { title: section?.section_title, isCurrent: true },
  ];

  return (
    <Container>
      <AppSection
        rightMenu={{
          isActive: true,
          id: section?.menu_id,
          activeItem: { id: section?.menu_item_id },
        }}
      >
        <Content ref={ref}>
          <Content.Header details={{ handlePrint }}>
            <Breadcrumb items={breadcrumbItems} />
          </Content.Header>
          <Content.Title>{section?.section_title}</Content.Title>
          <Content.Body className="flex flex-col gap-5" isShareable>
            {isLoading ? (
              <LinkBox.Loading count={5} />
            ) : sections?.data && sections?.data?.length > 0 ? (
              sections?.data?.map((item) => (
                <LinkBox
                  url={item?.link ? item?.link : `/sections/view/${item?.id}`}
                  target={item?.link ? '_blank' : '_self'}
                  key={item?.id}
                >
                  {item?.title}
                </LinkBox>
              ))
            ) : (
              <Empty />
            )}
            <Suspense>
              <PaginationWidget totalPage={sections?.total_page} />
            </Suspense>
          </Content.Body>
        </Content>
      </AppSection>
    </Container>
  );
}
